import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Button,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  TableContainer,
  Divider,
  InputAdornment,
  Collapse,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScheduleSelector from '../components/common/ScheduleSelector';
import FioriSidePanel from '../components/common/FioriSidePanel';
import { FIORI_COLORS } from '../components/common/FioriSidePanel';
import { useDispatch } from 'react-redux';
import { showSnackbar, setLoading } from '../store/slices/uiSlice';
import apiService from '../services/api.service';
import CopySchedulesDialog from '../components/schedules/CopySchedulesDialog';

interface CompanyRow { CompanyID: number; CompanyCode: string; CompanyName: string; DatabaseName: string; IsActive: boolean; }
interface Endpoint { key: string; label: string; source: 'sap' | 'db' }
interface ScheduleRow {
  scheduleid: number;
  name: string;
  companyid?: number | null;
  endpointkey: string;
  selectedfields: string[];
  condition?: string | null;
  datefield?: string | null;
  cronexpression: string;
  status: string;
  lastruntime?: string | null;
  nextruntime?: string | null;
  backfillchunkdays?: number | null;
  backfillstartdate?: string | null;
  backfillcursor?: string | null;
  backfillcomplete?: boolean | null;
}

const ScheduleJobs: React.FC = () => {
  const dispatch = useDispatch();

  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [fields, setFields] = useState<string[]>([]);
  const [schedules, setSchedules] = useState<ScheduleRow[]>([]);
  const [runsOpen, setRunsOpen] = useState(false);
  const [runsFor, setRunsFor] = useState<ScheduleRow | null>(null);
  const [runs, setRuns] = useState<any[]>([]);

  // Form state
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [scheduleName, setScheduleName] = useState('');
  const [endpointKey, setEndpointKey] = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [condition, setCondition] = useState<string>('');
  const [dateField, setDateField] = useState<string>('');
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleRow | null>(null);
  const [cronExpression, setCronExpression] = useState<string>('0 0 * * *'); // default daily at midnight
  const [backfillChunkDays, setBackfillChunkDays] = useState<string>(''); // '' or '0' = off
  const [backfillStartDate, setBackfillStartDate] = useState<string>(''); // yyyy-mm-dd
  const [scheduleSearch, setScheduleSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'Active' | 'Paused'>('all');
  const [endpointFilter, setEndpointFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  // A company with jobs is copied FROM; one with none yet (a new company) is copied TO.
  const copyFromHere = schedules.length > 0 || topBarCompanyId() === null;

  const dateFields = useMemo(() => fields.filter(f => /date|time/i.test(f)), [fields]);
  const dateFieldValues = useMemo(() => dateField.split(',').map((field) => field.trim()).filter(Boolean), [dateField]);
  const handleDateFieldChange = (value: unknown) => {
    const values = typeof value === 'string' ? value.split(',') : (value as string[]);
    setDateField(Array.from(new Set((values || []).map((field) => String(field || '').trim()).filter(Boolean))).join(','));
  };
  const allFieldsSelected = fields.length > 0 && selectedFields.length >= fields.length && fields.every((field) => selectedFields.includes(field));
  const handleSelectedFieldsChange = (value: unknown) => {
    const values = typeof value === 'string' ? value.split(',') : (value as string[]);
    if ((values || []).includes('__select_all_fields__')) {
      setSelectedFields(fields);
      return;
    }
    if ((values || []).includes('__clear_fields__')) {
      setSelectedFields([]);
      return;
    }
    setSelectedFields(Array.from(new Set((values || []).map((field) => String(field || '').trim()).filter(Boolean))));
  };
  const renderSelectedFieldValue = (selected: unknown) => {
    const values = selected as string[];
    if (values.length === 0) {
      return <Typography variant="caption" color="text.secondary">No fields selected</Typography>;
    }
    if (fields.length > 0 && values.length === fields.length) {
      return <Chip label={`All fields (${values.length})`} size="small" color="primary" variant="outlined" />;
    }
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {values.slice(0, 8).map((value) => <Chip key={value} label={value} size="small" />)}
        {values.length > 8 && <Chip label={`+${values.length - 8}`} size="small" />}
      </Box>
    );
  };
  const endpointLabelByKey = useMemo(() => new Map(endpoints.map((e) => [e.key, e.label])), [endpoints]);
  const companyNameById = useMemo(() => new Map(companies.map((c) => [c.CompanyID, c.CompanyName])), [companies]);
  const validCompanyIds = useMemo(() => new Set(companies.map((c) => Number(c.CompanyID)).filter((id) => Number.isFinite(id) && id > 0)), [companies]);
  const companySelectValue = companyId !== '' && validCompanyIds.has(Number(companyId)) ? companyId : '';
  const activeCount = schedules.filter((s) => s.status === 'Active').length;
  const pausedCount = schedules.filter((s) => s.status !== 'Active').length;
  const endpointCount = new Set(schedules.map((s) => s.endpointkey)).size;
  const formatDateTime = (value?: string | null) => value ? new Date(value).toLocaleString() : '-';
  const visibleSchedules = useMemo(() => {
    const query = scheduleSearch.trim().toLowerCase();
    return schedules.filter((s) => {
      const companyName = companyNameById.get(s.companyid || 0) || '-';
      const epLabel = endpointLabelByKey.get(s.endpointkey) || s.endpointkey;
      const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
      const matchesEndpoint = endpointFilter === 'all' || s.endpointkey === endpointFilter;
      const matchesQuery = !query || [
        s.name,
        String(s.scheduleid),
        companyName,
        epLabel,
        s.endpointkey,
        s.cronexpression,
        s.condition || '',
      ].some((value) => String(value).toLowerCase().includes(query));
      return matchesStatus && matchesEndpoint && matchesQuery;
    });
  }, [schedules, scheduleSearch, statusFilter, endpointFilter, companyNameById, endpointLabelByKey]);

  // Initialization is handled inside useEffect

  useEffect(() => {
    // Initialize schedules, endpoints, and companies on mount
    const init = async () => {
      try {
        dispatch(setLoading(true));
        await apiService.initSchedules();
        const [companiesRes, endpointsRes, schedulesRes] = await Promise.all([
          apiService.getCompanies(true),
          apiService.getScheduleEndpoints(),
          apiService.listSchedules(),
        ]);
        // Normalize companies payload shape (backend returns { success, data: [...] })
        const rawCompanies = Array.isArray(companiesRes) ? companiesRes : (companiesRes?.data || []);
        const normalizedCompanies = rawCompanies.map((c: any) => ({
          CompanyID: c.CompanyID ?? c.companyid,
          CompanyCode: c.CompanyCode ?? c.companycode,
          CompanyName: c.CompanyName ?? c.companyname,
          DatabaseName: c.DatabaseName ?? c.databasename,
          IsActive: c.IsActive ?? c.isactive,
        })).filter((c: CompanyRow) => Number(c.CompanyID) > 0);
        setCompanies(normalizedCompanies);
        setCompanyId((currentCompanyId) => {
          if (currentCompanyId === '') return '';
          return normalizedCompanies.some((c: CompanyRow) => Number(c.CompanyID) === Number(currentCompanyId)) ? currentCompanyId : '';
        });
        const eps = Array.isArray(endpointsRes) ? endpointsRes : (endpointsRes?.data || []);
        const hasWhs = eps.some((e: any) => e.key === 'sap-warehouses');
        setEndpoints(hasWhs ? eps : [...eps, { key: 'sap-warehouses', label: 'SAP B1 Warehouses', source: 'sap' }]);
        setSchedules(schedulesRes);
      } catch (error: any) {
        dispatch(showSnackbar({ message: error.message || 'Failed to load initial data', severity: 'error' }));
      } finally {
        dispatch(setLoading(false));
      }
    };
    init();
  }, [dispatch]);

  useEffect(() => {
    const loadFields = async () => {
      if (!endpointKey) return setFields([]);
      try {
        dispatch(setLoading(true));
        const res = await apiService.getScheduleFields(endpointKey, companySelectValue === '' ? undefined : Number(companySelectValue));
        const discovered = Array.isArray(res) ? res : (res?.data || []);
        if (endpointKey === 'sap-warehouses' && (!discovered || discovered.length === 0)) {
          setFields(['WarehouseCode','WarehouseName','Inactive','ExternallyManaged']);
        } else {
          setFields(discovered || []);
        }
        const dateCandidates = (discovered || []).filter((f: string) => /date|time/i.test(f));
        if (!dateField && dateCandidates.length) {
          const preferredOrder = ['UpdateDate', 'ModifiedDate', 'ModifyDate', 'CreationDate', 'CreateDate', 'CreateDateTime', 'CreationDateTime', 'UpdateTime', 'ModifiedTime', 'DocDate'];
          const picks = preferredOrder.filter(p => dateCandidates.includes(p)).slice(0, 2);
          setDateField((picks.length ? picks : [dateCandidates[0]]).join(','));
        }
      } catch (error: any) {
        dispatch(showSnackbar({ message: error.message || 'Failed to load fields', severity: 'error' }));
      } finally {
        dispatch(setLoading(false));
      }
    };
    loadFields();
  }, [endpointKey, companySelectValue, dispatch, dateField]);

  useEffect(() => {
    const reloadSchedules = async () => {
      try {
        const schedulesRes = await apiService.listSchedules();
        setSchedules(schedulesRes);
      } catch (error: any) {
        dispatch(showSnackbar({ message: error.message || 'Failed to reload schedules', severity: 'error' }));
      }
    };
    const onCompanyChanged = () => { void reloadSchedules(); };
    window.addEventListener('company-changed', onCompanyChanged as any);
    return () => { window.removeEventListener('company-changed', onCompanyChanged as any); };
  }, [dispatch]);

  const resetForm = () => {
    setCompanyId('');
    setScheduleName('');
    setEndpointKey('');
    setSelectedFields([]);
    setCondition('');
    setDateField('');
    setCronExpression('0 0 * * *');
    setBackfillChunkDays('');
    setBackfillStartDate('');
  };

  const refreshSchedules = async () => {
    try {
      const res = await apiService.listSchedules();
      setSchedules(res);
    } catch {}
  };

  const handleCreate = async () => {
    if (!scheduleName || !endpointKey || selectedFields.length === 0 || !cronExpression) {
      dispatch(showSnackbar({ message: 'Please fill all required fields and select at least one field', severity: 'warning' }));
      return;
    }
    try {
      dispatch(setLoading(true));
      await apiService.createSchedule({
        name: scheduleName,
        companyid: companyId === '' ? null : Number(companyId),
        endpointkey: endpointKey,
        selectedfields: selectedFields,
        condition: condition || undefined,
        datefield: dateField || undefined,
        cronexpression: cronExpression,
        backfillchunkdays: Number(backfillChunkDays) > 0 ? Number(backfillChunkDays) : null,
        backfillstartdate: Number(backfillChunkDays) > 0 && backfillStartDate ? backfillStartDate : null,
      });
      dispatch(showSnackbar({ message: 'Schedule created successfully', severity: 'success' }));
      resetForm();
      setCreateOpen(false);
      await refreshSchedules();
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to create schedule', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRunNow = async (id: number) => {
    try {
      dispatch(setLoading(true));
      const res = await apiService.runSchedule(id);

      // Handle response - success can have error message (e.g., "skipped" scenarios)
      if (res.success) {
        const message = res.error
          ? `${res.error} (Rows: ${res.rows ?? 0})`
          : `Run completed successfully. Rows: ${res.rows ?? 0}`;
        dispatch(showSnackbar({ message, severity: 'info' }));
      } else {
        dispatch(showSnackbar({ message: res.error || 'Run failed', severity: 'error' }));
      }

      await refreshSchedules();
    } catch (error: any) {
      // A failed run answers 400 with { success: false, error }. Without this the
      // user only ever saw axios's "Request failed with status code 400" and the
      // actual SAP/DB reason stayed buried in the run history.
      const message = error.response?.data?.error || error.message || 'Failed to run schedule';
      dispatch(showSnackbar({ message, severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const openRuns = async (s: ScheduleRow) => {
    try {
      dispatch(setLoading(true));
      const res = await apiService.getScheduleRuns(s.scheduleid);
      setRuns(res || []);
      setRunsFor(s);
      setCreateOpen(false);
      setRunsOpen(true);
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to load runs', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleToggleStatus = async (s: ScheduleRow) => {
    try {
      dispatch(setLoading(true));
      const newStatus = s.status === 'Active' ? 'Paused' : 'Active';
      await apiService.updateSchedule(s.scheduleid, { status: newStatus });
      dispatch(showSnackbar({ message: `Schedule ${newStatus === 'Active' ? 'activated' : 'paused'}`, severity: 'success' }));
      await refreshSchedules();
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to update schedule', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const openEdit = (s: ScheduleRow) => {
    setEditing(s);
    setScheduleName(s.name);
    setEndpointKey(s.endpointkey);
    setCompanyId(s.companyid ?? '');
    setSelectedFields(s.selectedfields || []);
    setCondition(s.condition || '');
    setDateField(s.datefield || '');
    setCronExpression(s.cronexpression);
    setBackfillChunkDays(s.backfillchunkdays ? String(s.backfillchunkdays) : '');
    setBackfillStartDate(s.backfillstartdate ? String(s.backfillstartdate).slice(0, 10) : '');
    setCreateOpen(false);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      dispatch(setLoading(true));
      await apiService.updateSchedule(editing.scheduleid, {
        name: scheduleName,
        selectedfields: selectedFields,
        condition,
        datefield: dateField,
        cronexpression: cronExpression,
        backfillchunkdays: Number(backfillChunkDays) > 0 ? Number(backfillChunkDays) : 0,
        backfillstartdate: Number(backfillChunkDays) > 0 && backfillStartDate ? backfillStartDate : null,
      });
      setEditOpen(false);
      await refreshSchedules();
      dispatch(showSnackbar({ message: 'Schedule updated', severity: 'success' }));
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to update schedule', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDelete = async (s: ScheduleRow) => {
    try {
      const ok = window.confirm(`Delete schedule "${s.name}"?`);
      if (!ok) return;
      dispatch(setLoading(true));
      await apiService.deleteSchedule(s.scheduleid);
      dispatch(showSnackbar({ message: 'Schedule deleted', severity: 'success' }));
      await refreshSchedules();
    } catch (error: any) {
      dispatch(showSnackbar({ message: error.message || 'Failed to delete schedule', severity: 'error' }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Box
      component="section"
      aria-labelledby="sj-heading"
      sx={{
        p: { xs: 1, sm: 1.25, md: 1.5 },
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        '& .MuiInputBase-root': { minHeight: 30 },
        '& .MuiInputBase-input': { py: '5px', fontSize: 12.5 },
        '& .MuiInputLabel-root': { fontSize: 11.5 },
        '& .MuiButton-root': { minHeight: 28, px: 1.25, fontSize: 12 },
        '& .MuiChip-root': { height: 20, fontSize: 11 },
        '& .MuiTableCell-root': { py: 0.5, px: 0.75, fontSize: 12 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          mb: 1,
          p: 1,
          borderRadius: 1,
          borderColor: '#d9e1e7',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          background: 'linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h1" id="sj-heading" variant="h6" sx={{ fontSize: 18, fontWeight: 700, lineHeight: 1.1 }}>
              Schedule Jobs
            </Typography>
            <Typography variant="caption" color="text.secondary">
              SAP B1 sync orchestration across company databases
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ minWidth: 0, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Chip label={`Total ${schedules.length}`} color="default" variant="outlined" />
            <Chip label={`Active ${activeCount}`} color="success" variant="outlined" />
            <Chip label={`Paused ${pausedCount}`} color="warning" variant="outlined" />
            <Chip label={`Endpoints ${endpointCount}`} color="info" variant="outlined" />
            <Button
              variant={createOpen ? 'contained' : 'outlined'}
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateOpen((open) => !open);
                setEditOpen(false);
              }}
            >
              {createOpen ? 'Hide Create' : 'New Schedule'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => setCopyOpen(true)}
              disabled={companies.length < 2}
            >
              {copyFromHere ? 'Copy to company' : 'Copy from company'}
            </Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshSchedules}>
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Collapse in={createOpen && !editOpen && !runsOpen} timeout={180} unmountOnExit>
      <Paper sx={{ p: 1, mb: 1.25, borderRadius: 1, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }} elevation={0}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
            Create Schedule
          </Typography>
          <Divider flexItem sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Leave Date/Time blank to use backend incremental defaults
          </Typography>
        </Stack>
        <Grid container spacing={0.75}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="company-select-label">Company DB</InputLabel>
              <Select
                labelId="company-select-label"
                value={companySelectValue}
                label="Company DB"
                onChange={(e) => setCompanyId(e.target.value as any)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.CompanyID} value={c.CompanyID}>
                    {c.CompanyName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              label="Schedule Name"
              fullWidth
              size="small"
              value={scheduleName}
              onChange={(e) => setScheduleName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="endpoint-select-label">SAP B1 Endpoint</InputLabel>
              <Select
                labelId="endpoint-select-label"
                value={endpointKey}
                label="SAP B1 Endpoint"
                onChange={(e) => setEndpointKey(e.target.value as string)}
              >
                {endpoints.map((ep) => (
                  <MenuItem key={ep.key} value={ep.key}>{ep.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <ScheduleSelector
              value={cronExpression}
              onChange={setCronExpression}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Condition"
              fullWidth
              size="small"
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              placeholder="Optional business filter only, e.g. DocumentStatus eq 'bost_Open'"
            />
            <Typography variant="caption" color="text.secondary">
              Do not add Create/Update date here. Incremental date filtering is built from the tracking fields below.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Backfill chunk (days)"
                type="number"
                size="small"
                value={backfillChunkDays}
                onChange={(e) => setBackfillChunkDays(e.target.value)}
                placeholder="0 = off"
                inputProps={{ min: 0 }}
                sx={{ width: 170 }}
              />
              <TextField
                label="Backfill start date"
                type="date"
                size="small"
                value={backfillStartDate}
                onChange={(e) => setBackfillStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={!(Number(backfillChunkDays) > 0)}
                sx={{ width: 190 }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Optional: load history in N-day chunks (one per cycle) from the start date up to today, then switch to normal daily incremental. 0 = off.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="datefield-select-label">Incremental Tracking Fields</InputLabel>
              <Select
                labelId="datefield-select-label"
                multiple
                value={dateFieldValues}
                label="Incremental Tracking Fields"
                onChange={(e) => handleDateFieldChange(e.target.value)}
                input={<OutlinedInput label="Incremental Tracking Fields" sx={{ minHeight: 30, py: 0.25 }} />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).length === 0
                      ? <Typography variant="caption">Backend default</Typography>
                      : (selected as string[]).map((value) => <Chip key={value} label={value} size="small" />)}
                  </Box>
                )}
              >
                {dateFields.map((f) => (
                  <MenuItem key={f} value={f} sx={{ minHeight: 26, py: 0.25 }}>
                    <Checkbox size="small" sx={{ p: 0.25 }} checked={dateFieldValues.indexOf(f) > -1} />
                    <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={f} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              Select UpdateDate and CreationDate where available, so both new back-dated records and later modifications are captured.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="fields-select-label">Fields</InputLabel>
              <Select
                labelId="fields-select-label"
                multiple
                value={selectedFields}
                onChange={(e) => handleSelectedFieldsChange(e.target.value)}
                input={<OutlinedInput label="Fields" sx={{ minHeight: 30, py: 0.25 }} />}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 240 } },
                  MenuListProps: { sx: { '& .MuiMenuItem-root': { minHeight: 26, py: 0.25 }, '& .MuiCheckbox-root': { p: 0.25 }, '& .MuiListItemText-root': { m: 0 } } }
                }}
                renderValue={renderSelectedFieldValue}
              >
                <MenuItem value="__select_all_fields__" disabled={fields.length === 0} sx={{ minHeight: 28, py: 0.25, bgcolor: '#f6f8fb' }}>
                  <Checkbox size="small" sx={{ p: 0.25 }} checked={allFieldsSelected} indeterminate={selectedFields.length > 0 && !allFieldsSelected} />
                  <ListItemText primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }} primary={`Select all fields${fields.length ? ` (${fields.length})` : ''}`} />
                </MenuItem>
                <MenuItem value="__clear_fields__" disabled={selectedFields.length === 0} sx={{ minHeight: 28, py: 0.25, bgcolor: '#f6f8fb' }}>
                  <Checkbox size="small" sx={{ p: 0.25 }} checked={false} />
                  <ListItemText primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }} primary="Clear selection" />
                </MenuItem>
                <Divider />
                {fields.map((f) => (
                  <MenuItem key={f} value={f} sx={{ minHeight: 26, py: 0.25 }}>
                    <Checkbox size="small" sx={{ p: 0.25 }} checked={selectedFields.indexOf(f) > -1} />
                    <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={f} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              {fields.length ? `${selectedFields.length}/${fields.length} fields selected. Use Select all fields to save time.` : 'Select an endpoint to discover fields.'}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="primary" onClick={handleCreate}>Add Schedule</Button>
              <Button variant="outlined" onClick={resetForm}>Clear</Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
      </Collapse>

      <Paper sx={{ p: 1, borderRadius: 1, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }} elevation={0}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} spacing={0.75} sx={{ mb: 0.75, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 130 }}>
            Existing Schedules
          </Typography>
          <TextField
            placeholder="Search schedule, company, endpoint, cron..."
            value={scheduleSearch}
            onChange={(e) => setScheduleSearch(e.target.value)}
            size="small"
            sx={{ minWidth: { xs: '100%', md: 360 }, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value as any)}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Paused">Paused</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Endpoint</InputLabel>
            <Select value={endpointFilter} label="Endpoint" onChange={(e) => setEndpointFilter(e.target.value as string)}>
              <MenuItem value="all">All Endpoints</MenuItem>
              {endpoints.map((ep) => (
                <MenuItem key={ep.key} value={ep.key}>{ep.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <TableContainer sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto', maxHeight: createOpen ? 'calc(100vh - 360px)' : 'calc(100vh - 210px)', minHeight: 300, border: '1px solid #e3e8ee', borderRadius: 1 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 1120 }}>
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f3f6f9', fontWeight: 700, color: '#1f2d3d', borderBottom: '1px solid #cfd8dc' } }}>
              <TableCell sx={{ width: 220 }}>Schedule</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Endpoint</TableCell>
              <TableCell sx={{ width: 210 }}>Fields</TableCell>
              <TableCell sx={{ width: 115 }}>Cron</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Next Run</TableCell>
              <TableCell sx={{ width: 180 }}>Incremental</TableCell>
              <TableCell align="right" sx={{ width: 150 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleSchedules.map((s) => {
              const companyName = companyNameById.get(s.companyid || 0) || '-';
              const epLabel = endpointLabelByKey.get(s.endpointkey) || s.endpointkey;
              return (
                <TableRow key={s.scheduleid} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fbfcfd' } }}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>{s.name}</Typography>
                    <Typography variant="caption" color="text.secondary">ID {s.scheduleid}</Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={companyName}>
                      <Typography variant="caption" sx={{ display: 'block', maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {companyName}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip label={epLabel} variant="outlined" size="small" sx={{ maxWidth: 220, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {s.selectedfields?.slice(0, 3).map((f) => (<Chip key={f} label={f} size="small" variant="outlined" />))}
                      {(s.selectedfields?.length || 0) > 3 && <Chip label={`+${(s.selectedfields?.length || 0) - 3}`} size="small" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'Consolas, monospace' }}>{s.cronexpression}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={s.status} color={s.status === 'Active' ? 'success' : 'default'} size="small" variant={s.status === 'Active' ? 'filled' : 'outlined'} />
                  </TableCell>
                  <TableCell>{formatDateTime(s.lastruntime)}</TableCell>
                  <TableCell>{formatDateTime(s.nextruntime)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ display: 'block' }}>{s.datefield || 'Backend default'}</Typography>
                    {s.condition && (
                      <Tooltip title={s.condition}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.condition}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <Tooltip title="Run Now">
                        <IconButton size="small" onClick={() => handleRunNow(s.scheduleid)}>
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={s.status === 'Active' ? 'Pause' : 'Activate'}>
                        <IconButton size="small" onClick={() => handleToggleStatus(s)}>
                          {s.status === 'Active' ? <PauseCircleOutlineIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(s)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Runs">
                        <IconButton size="small" onClick={() => openRuns(s)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(s)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleSchedules.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography variant="body2" color="text.secondary">No schedules found for the current filter.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>

      {editOpen && (
        <FioriSidePanel
          open={editOpen}
          onClose={() => setEditOpen(false)}
          title="Edit Schedule"
          subtitle={`Editing: ${editing?.name || ''}`}
          width={650}
          actions={
            <>
              <Button
                onClick={() => setEditOpen(false)}
                size="small"
                sx={{
                  color: FIORI_COLORS.textSecondary,
                  '&:hover': {
                    backgroundColor: '#F0F0F0'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={saveEdit}
                size="small"
                sx={{
                  backgroundColor: FIORI_COLORS.primary,
                  minWidth: 80,
                  '&:hover': {
                    backgroundColor: FIORI_COLORS.primaryHover
                  }
                }}
              >
                Save
              </Button>
            </>
          }
        >
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <TextField label="Schedule Name" fullWidth size="small" value={scheduleName} onChange={(e) => setScheduleName(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="endpoint-edit-label">Endpoint</InputLabel>
                <Select labelId="endpoint-edit-label" value={endpointKey} label="Endpoint" onChange={(e) => setEndpointKey(e.target.value as string)}>
                  {endpoints.map((ep) => (<MenuItem key={ep.key} value={ep.key}>{ep.label}</MenuItem>))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="datefield-edit-label">Incremental Fields</InputLabel>
                <Select
                  labelId="datefield-edit-label"
                  multiple
                  value={dateFieldValues}
                  label="Incremental Fields"
                  onChange={(e) => handleDateFieldChange(e.target.value)}
                  input={<OutlinedInput label="Incremental Fields" sx={{ minHeight: 32, py: 0.25 }} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (<Chip key={value} label={value} size="small" />))}
                    </Box>
                  )}
                >
                  {fields.filter(f => /date|time/i.test(f)).map((f) => (
                    <MenuItem key={f} value={f} sx={{ minHeight: 26, py: 0.25 }}>
                      <Checkbox size="small" sx={{ p: 0.25 }} checked={dateFieldValues.indexOf(f) > -1} />
                      <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={f} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <ScheduleSelector value={cronExpression} onChange={setCronExpression} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel id="fields-edit-label">Fields</InputLabel>
                <Select
                  labelId="fields-edit-label"
                  multiple
                  value={selectedFields}
                  onChange={(e) => handleSelectedFieldsChange(e.target.value)}
                  input={<OutlinedInput label="Fields" sx={{ minHeight: 32, py: 0.25 }} />}
                  MenuProps={{
                    PaperProps: { sx: { maxHeight: 240 } },
                    MenuListProps: { sx: { '& .MuiMenuItem-root': { minHeight: 26, py: 0.25 }, '& .MuiCheckbox-root': { p: 0.25 }, '& .MuiListItemText-root': { m: 0 } } }
                  }}
                  renderValue={renderSelectedFieldValue}
                >
                  <MenuItem value="__select_all_fields__" disabled={fields.length === 0} sx={{ minHeight: 28, py: 0.25, bgcolor: '#f6f8fb' }}>
                    <Checkbox size="small" sx={{ p: 0.25 }} checked={allFieldsSelected} indeterminate={selectedFields.length > 0 && !allFieldsSelected} />
                    <ListItemText primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }} primary={`Select all fields${fields.length ? ` (${fields.length})` : ''}`} />
                  </MenuItem>
                  <MenuItem value="__clear_fields__" disabled={selectedFields.length === 0} sx={{ minHeight: 28, py: 0.25, bgcolor: '#f6f8fb' }}>
                    <Checkbox size="small" sx={{ p: 0.25 }} checked={false} />
                    <ListItemText primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }} primary="Clear selection" />
                  </MenuItem>
                  <Divider />
                  {fields.map((f) => (
                    <MenuItem key={f} value={f} sx={{ minHeight: 26, py: 0.25 }}>
                      <Checkbox size="small" sx={{ p: 0.25 }} checked={selectedFields.indexOf(f) > -1} />
                      <ListItemText primaryTypographyProps={{ variant: 'caption' }} primary={f} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Condition" fullWidth size="small" value={condition} onChange={(e) => setCondition(e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Backfill chunk (days)"
                  type="number"
                  size="small"
                  value={backfillChunkDays}
                  onChange={(e) => setBackfillChunkDays(e.target.value)}
                  placeholder="0 = off"
                  inputProps={{ min: 0 }}
                  sx={{ width: 170 }}
                />
                <TextField
                  label="Backfill start date"
                  type="date"
                  size="small"
                  value={backfillStartDate}
                  onChange={(e) => setBackfillStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  disabled={!(Number(backfillChunkDays) > 0)}
                  sx={{ width: 190 }}
                />
                {editing?.backfillchunkdays && !editing?.backfillcomplete && editing?.backfillcursor && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                    Backfilling… up to {String(editing.backfillcursor).slice(0, 10)}
                  </Typography>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Optional: load history in N-day chunks (one per cycle) from the start date up to today, then normal daily incremental. Set 0 to disable. Changing this restarts the backfill.
              </Typography>
            </Grid>
          </Grid>
        </FioriSidePanel>
      )}

      {runsOpen && (
        <FioriSidePanel
          open={runsOpen}
          onClose={() => setRunsOpen(false)}
          title="Schedule Runs"
          subtitle={`History for: ${runsFor?.name || ''}`}
          width={800}
          actions={
            <Button
              onClick={() => setRunsOpen(false)}
              size="small"
              sx={{
                color: FIORI_COLORS.textSecondary,
                '&:hover': {
                  backgroundColor: '#F0F0F0'
                }
              }}
            >
              Close
            </Button>
          }
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>RunID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rows</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Finished</TableCell>
                <TableCell>Payload</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.runid}>
                  <TableCell>{r.runid}</TableCell>
                  <TableCell>
                    <Chip label={r.status} color={r.status === 'Success' ? 'success' : (r.status === 'Running' ? 'info' : 'error')} size="small" />
                  </TableCell>
                  <TableCell>{r.rowscount ?? '-'}</TableCell>
                  <TableCell>{r.startedat ? new Date(r.startedat).toLocaleString() : '-'}</TableCell>
                  <TableCell>{r.finishedat ? new Date(r.finishedat).toLocaleString() : '-'}</TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ display: 'block', maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {JSON.stringify(r.requestpayload || {})}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.errormessage || JSON.stringify(r.resultpayload || {})}>
                      <Typography variant="caption" sx={{ display: 'block', maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: r.errormessage ? 'error.main' : 'inherit' }}>
                        {r.errormessage || JSON.stringify(r.resultpayload || {})}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {runs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No runs yet.</Typography></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </FioriSidePanel>
      )}
      <CopySchedulesDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        companies={companies}
        endpointLabel={(key) => endpointLabelByKey.get(key) || key}
        defaultFromCompanyId={copyFromHere ? topBarCompanyId() : null}
        defaultToCompanyId={copyFromHere ? null : topBarCompanyId()}
        onCopied={(message) => {
          setCopyOpen(false);
          dispatch(showSnackbar({ message, severity: 'success' }));
          void refreshSchedules();
        }}
      />
    </Box>
  );
};

/** The company picked in the top bar (the page lists its jobs), if any. */
function topBarCompanyId(): number | null {
  try {
    const id = Number(localStorage.getItem('selectedCompanyId') || '');
    return Number.isInteger(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export default ScheduleJobs;
